import { logAmplitudeEvent } from 'amplitude/amplitude'
import { CircularProgress, Grid, Icon, styled, Tooltip } from 'opensolar-ui'
import { useContext } from 'react'
import { useTranslate } from 'react-admin'
import { useField } from 'react-final-form'
import type { FieldLabelEndAdornment, InlineIconPreviewEndAdornment } from '../types'
import { ClassicProposalPreviewContext } from './ClassicProposalPreviewContext'

const IconButton = styled('span')(({ disabled }: { disabled: boolean }) => ({
  cursor: disabled ? 'not-allowed' : 'pointer',
  // `Tooltip` swallows pointer events on disabled children, so wrap in a span to keep hover
  // detection working while the button is disabled (empty field or dialog already open).
  display: 'inline-flex',
}))

export const CustomFieldTitle = ({
  label,
  fieldName,
  endAdornment,
}: {
  label: string
  fieldName: string
  endAdornment: FieldLabelEndAdornment
}) => {
  const { available } = useContext(ClassicProposalPreviewContext)
  const translate = useTranslate()
  return (
    <Grid container justify="space-between" alignItems="center">
      <Grid item>{translate(label)}</Grid>
      {endAdornment?.action === 'preview' && endAdornment?.adornment === 'icon' && available && (
        <InlineDialogPreviewIconButton preview={endAdornment} fieldName={fieldName} />
      )}
    </Grid>
  )
}

/**
 * Strips HTML tags and whitespace to decide whether the field's current form value would render
 * anything meaningful in the dialog. Catches the draft-editor "empty" sentinels (`<p></p>`,
 * `<p><br></p>`) that a naive `!value` check would treat as populated.
 */
const isFieldValueEmpty = (value: unknown): boolean => {
  if (value == null) return true
  if (typeof value !== 'string') return false
  return (
    value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length === 0
  )
}

export const InlineDialogPreviewIconButton = ({
  preview,
  fieldName,
}: {
  preview: InlineIconPreviewEndAdornment
  fieldName: string
}) => {
  const translate = useTranslate()
  const { available, showDialog, previewIsDirty, refresh, isLoading } = useContext(ClassicProposalPreviewContext)
  // Subscribe only to this field's value so the button doesn't re-render on every keystroke
  // elsewhere in the form (unlike useFormState({ values: true })).
  const {
    input: { value: fieldValue },
  } = useField(fieldName, { subscription: { value: true } })

  if (!available) return null

  const isEmpty = !!preview.disableWhenEmpty && isFieldValueEmpty(fieldValue)
  const isDisabled = isEmpty

  const handleClick = async () => {
    if (isDisabled) return
    const triggeredRefresh = previewIsDirty
    if (previewIsDirty && refresh) {
      await refresh()
    }
    logAmplitudeEvent('classic_proposal_inline_field_preview_opened', {
      dialog_name: preview.dialog_name,
      source: 'classic_proposal_preview',
      triggered_refresh: triggeredRefresh,
    })
    showDialog(preview.dialog_name)
  }

  const tooltipTitle = isEmpty
    ? translate('Nothing to preview yet — add content to enable this.')
    : translate(preview.tooltips)

  return (
    <Tooltip title={tooltipTitle}>
      <IconButton disabled={isDisabled} onClick={handleClick}>
        {isLoading ? <CircularProgress size={20} /> : <Icon icon={'preview'} />}
      </IconButton>
    </Tooltip>
  )
}
